import React, { useEffect, useState } from "react";
import { Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField, Typography } from "@mui/material";
import moment from "moment";
import generatedFileServices, { requestErrors } from "app/services/automations/generated-file-services";
import storeServices from "app/services/automations/store-services";
import { SaveButton } from "app/shared/ui";

const DATE = "YYYY-MM-DD";

// Pide los datos de un local (plugin «Descarga de datos»). Se abre desde «Descargas» o desde el menú de un local, que lo
// trae elegido. El archivo se prepara en segundo plano y llega un correo cuando está listo.
const DataExportDialog = ({ open, onClose, store = null, onRequested }) => {
    const [stores, setStores] = useState([]);
    const [limits, setLimits] = useState(null);
    const [form, setForm] = useState({ store_id: "", from: "", to: "" });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        const maxDays = 31;
        setForm({ store_id: store?.id || "", from: moment().subtract(maxDays - 1, "days").format(DATE), to: moment().format(DATE) });
        setError(null);
        generatedFileServices.list().then((resp) => setLimits(resp?.meta?.data_export || null)).catch(() => setLimits(null));
        if (!store) storeServices.getStores("?per_page=200").then((resp) => setStores(resp?.data || [])).catch(() => setStores([]));
    }, [open, store]);

    useEffect(() => {
        if (!limits) return;
        // Que el período por defecto respete el máximo y la retención de la empresa.
        setForm((current) => {
            const earliest = moment(limits.earliest_date, DATE);
            const from = moment.max(moment(current.to, DATE).subtract(limits.max_days - 1, "days"), earliest);
            return { ...current, from: from.format(DATE) };
        });
    }, [limits]);

    const submit = async () => {
        if (!form.store_id) {
            setError("Elige un local.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const file = await generatedFileServices.requestStoreData(form);
            onRequested?.(file);
            onClose();
        } catch (requestError) {
            setError(requestErrors(requestError, "No se pudo pedir la descarga."));
        } finally {
            setSaving(false);
        }
    };

    const selectedStore = store || stores.find((option) => `${option.id}` === `${form.store_id}`) || null;
    const noneLeft = limits && limits.remaining_today === 0;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Descargar datos{store ? ` de ${store.name}` : ""}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                        Todas las lecturas de los dispositivos del local en un archivo CSV (se abre en Excel), comprimido en ZIP.
                        Hasta {limits?.max_days || 31} días por descarga. Te avisamos por correo cuando esté lista.
                    </Typography>
                    {/* La Grid va dentro de un Box: como hija directa del Stack perdería su margen negativo y se correría. */}
                    <Box>
                    <Grid container spacing={2}>
                        {!store && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    options={stores}
                                    value={selectedStore}
                                    getOptionLabel={(option) => option?.name || ""}
                                    onChange={(event, value) => setForm({ ...form, store_id: value?.id || "" })}
                                    renderInput={(params) => <TextField {...params} required label="Local" />}
                                />
                            </Grid>
                        )}
                        <Grid item xs={6}>
                            <TextField
                                fullWidth type="date" label="Desde" InputLabelProps={{ shrink: true }} value={form.from}
                                inputProps={{ min: limits?.earliest_date, max: form.to }}
                                onChange={(event) => setForm({ ...form, from: event.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={form.to}
                                inputProps={{ min: form.from, max: moment().format(DATE) }}
                                onChange={(event) => setForm({ ...form, to: event.target.value })}
                            />
                        </Grid>
                    </Grid>
                    </Box>
                    {limits && (
                        <Typography variant="caption" color={noneLeft ? "error.main" : "text.secondary"}>
                            {noneLeft
                                ? `Tu empresa ya hizo las ${limits.daily_limit} descargas de hoy. Vuelve a intentarlo mañana.`
                                : `Quedan ${limits.remaining_today} de ${limits.daily_limit} descargas hoy para tu empresa. Tus datos se conservan ${limits.retention_days} días.`}
                        </Typography>
                    )}
                    {!!error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancelar</Button>
                <SaveButton loading={saving} disabled={saving || noneLeft} onClick={submit}>Pedir descarga</SaveButton>
            </DialogActions>
        </Dialog>
    );
};

export default DataExportDialog;
